import moment from '@e3dc-react/shell/Libs/moment'
import { useEffect, useState } from 'react'
import useDataForBatteryWidget, { BatteryWidgetData } from './use-data-for-battery-widget'
import useDataForDppWidget, { DynamicPowerPriceWidgetData } from './use-data-for-dpp-widget'
import useDataForWallboxWidgets, { WallboxWidgetsData } from './use-data-for-wallbox-widgets'
import useDataForPvForecastWidget from './use-data-for-pv-forecast-widget'
import useInstallationWidgetsAmount from './use-installation-widgets-amount'
import type { PvForecastWidgetData } from '../types'

const MAXIMUM_LOADING_TIME = moment.duration(8, 'seconds').asMilliseconds()

export type UseFetchDataForWidgets = {
  pvForecastWidgetData: PvForecastWidgetData
  dynamicPowerPriceWidgetData: DynamicPowerPriceWidgetData
  batteryWidgetData: BatteryWidgetData
  wallboxWidgetsData: WallboxWidgetsData
  isWidgetDataLoading: boolean
  amountOfWidgetsToBeDisplayed: number | null
}
/*
 * A hook that fetches the data for the widgets on the dashboard.
 */
const useFetchDataForWidgets = ({ installationId, storageId }: { installationId?: string; storageId?: string }): UseFetchDataForWidgets => {
  /* Pv Forecast Widget Data */
  const pvForecastWidgetData = useDataForPvForecastWidget()
  const { isPvForecastWidgetDataLoading } = pvForecastWidgetData

  /* Dynamic Power Price Widget Data */
  const dynamicPowerPriceWidgetData = useDataForDppWidget({ installationId, storageId })
  const { isDynamicPowerPriceWidgetDataLoading } = dynamicPowerPriceWidgetData

  /* Battery Widget Data */
  const batteryWidgetData = useDataForBatteryWidget()
  const { isBatteryWidgetDataLoading } = batteryWidgetData

  /* Wallbox Widget Data */
  const wallboxWidgetsData = useDataForWallboxWidgets({
    installationId,
    storageId,
    currentPowerPrice: dynamicPowerPriceWidgetData.currentPowerPriceValues.price,
    wallboxLimit: Number(dynamicPowerPriceWidgetData.dynamicPricesSettings?.priceLimitWallbox) || 0,
  })
  const { isWallboxWidgetDataLoading } = wallboxWidgetsData

  /* Combined widget loading state  */
  const [maximumLoadingTimeReached, setMaximumLoadingTimeReached] = useState(false)
  const isWidgetDataLoading = maximumLoadingTimeReached
    ? false
    : isPvForecastWidgetDataLoading || isDynamicPowerPriceWidgetDataLoading || isBatteryWidgetDataLoading || isWallboxWidgetDataLoading

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMaximumLoadingTimeReached(true)
    }, MAXIMUM_LOADING_TIME)

    return () => {
      setMaximumLoadingTimeReached(false)
      clearTimeout(timeout)
    }
  }, [installationId, storageId])

  /* Amount of widgets visible  */
  const amountOfWidgetsToBeDisplayed = useInstallationWidgetsAmount({ installationId, storageId, isWidgetDataLoading, wallboxWidgetsData })

  return {
    pvForecastWidgetData,
    dynamicPowerPriceWidgetData,
    batteryWidgetData,
    wallboxWidgetsData,
    isWidgetDataLoading,
    amountOfWidgetsToBeDisplayed,
  }
}

export default useFetchDataForWidgets
